import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Card,
  Table,
  Tag,
  Typography,
  Space,
  Button,
  Tooltip,
  Empty,
  Row,
  Col,
  Statistic,
  message,
} from 'antd';
import {
  CheckCircleOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
  DownloadOutlined,
  CreditCardOutlined,
  BarcodeOutlined,
  QrcodeOutlined,
  DollarOutlined,
  SyncOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { supabase } from '../../../shared/lib/supabase';
import { useAuthStore } from '@/features/auth/store/authStore';
import dayjs from 'dayjs';
import 'dayjs/locale/pt-br';
import relativeTime from 'dayjs/plugin/relativeTime';

dayjs.extend(relativeTime);
dayjs.locale('pt-br');

const { Title, Text } = Typography;

interface Payment {
  id: string;
  amount: number;
  currency: string;
  status: string;
  billing_type?: string;
  payment_method?: string;
  description?: string;
  invoice_url?: string;
  bank_slip_url?: string;
  pix_qr_code_payload?: string;
  due_date?: string;
  paid_at?: string;
  created_at: string;
  subscription_id?: string;
  client_subscriptions?: {
    products?: {
      name: string;
    };
  };
}

export const PaymentHistoryPage: React.FC = () => {
  const [isSyncing, setIsSyncing] = useState(false);
  const { user } = useAuthStore();
  const userId = user?.id;

  // Fetch payments
  const { data: payments, isLoading, refetch } = useQuery<Payment[]>({
    queryKey: ['payments', userId],
    enabled: !!userId,
    queryFn: async () => {
      if (!userId) return [];

      // Buscar TODOS os payments da tabela payments (incluindo os sem subscription)
      const { data: paymentsData, error: paymentsError } = await supabase
        .from('payments')
        .select(`
          *,
          client_subscriptions(
            product_id,
            products(name)
          )
        `)
        .eq('user_id', userId)
        .order('due_date', { ascending: false });

      if (paymentsError) {
        console.error('Error fetching payments:', paymentsError);
      }

      console.log('Payments from database:', paymentsData?.length || 0);
      console.log('Payments data:', paymentsData);

      // Se já temos payments da tabela, retorná-los
      if (paymentsData && paymentsData.length > 0) {
        return paymentsData;
      }

      // Se não há payments, buscar assinaturas como fallback
      const { data: subscriptionsData, error: subscriptionsError } = await supabase
        .from('client_subscriptions')
        .select(`
          id,
          product_id,
          payment_link_url,
          asaas_payment_id,
          billing_type,
          next_due_date,
          payment_status,
          created_at,
          products(
            name,
            monthly_price,
            setup_fee
          )
        `)
        .eq('user_id', userId)
        .not('payment_link_url', 'is', null)
        .order('created_at', { ascending: false });

      if (subscriptionsError) {
        console.error('Error fetching subscriptions:', subscriptionsError);
      }

      console.log('Subscriptions data (fallback):', subscriptionsData?.length || 0);

      // Criar payments virtuais a partir das subscriptions
      const virtualPayments: Payment[] = [];
      subscriptionsData?.forEach(sub => {
        const product = Array.isArray(sub.products) ? sub.products[0] : sub.products;
        const monthlyPrice = product?.monthly_price || 0;
        const setupFee = product?.setup_fee || 0;
        const totalAmount = monthlyPrice + setupFee;

        let mappedStatus = sub.payment_status || 'pending';
        if (mappedStatus === 'paid' || mappedStatus === 'confirmed' || mappedStatus === 'received' || mappedStatus === 'RECEIVED' || mappedStatus === 'CONFIRMED') {
          mappedStatus = 'succeeded';
        } else if (mappedStatus === 'PENDING') {
          mappedStatus = 'pending';
        } else if (mappedStatus === 'OVERDUE') {
          mappedStatus = 'overdue';
        }

        virtualPayments.push({
          id: sub.id,
          amount: totalAmount,
          currency: 'BRL',
          status: mappedStatus,
          billing_type: sub.billing_type,
          description: product?.name,
          invoice_url: sub.payment_link_url || undefined,
          bank_slip_url: sub.payment_link_url || undefined,
          due_date: sub.next_due_date || undefined,
          created_at: sub.created_at,
          subscription_id: sub.id,
          client_subscriptions: {
            products: product ? { name: product.name } : { name: 'Produto não identificado' },
          },
        });
      });

      return virtualPayments;
    },
  });

  // Sync with Asaas
  const syncWithAsaas = async () => {
    setIsSyncing(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('Não autenticado');
      }

      console.log('Iniciando sincronização com Asaas...');
      
      const { data, error } = await supabase.functions.invoke('sync-asaas-payments', {
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
      });

      console.log('Resposta da sincronização:', data);
      console.log('Erro da sincronização:', error);

      if (error) {
        throw error;
      }

      message.success(data.message || `${data.synced} pagamentos sincronizados com sucesso`);
      
      // Aguardar um pouco e recarregar os dados
      console.log('Aguardando 1 segundo antes de recarregar...');
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      console.log('Recarregando dados...');
      await refetch();
      
      console.log('Dados recarregados!');
    } catch (error: any) {
      console.error('Sync error:', error);
      message.error('Erro ao sincronizar pagamentos: ' + (error.message || 'Erro desconhecido'));
    } finally {
      setIsSyncing(false);
    }
  };

  // Calculate statistics
  const totalPaid = payments?.filter(p => p.status === 'succeeded').reduce((sum, p) => sum + p.amount, 0) || 0;
  const totalPending = payments?.filter(p => p.status === 'pending').reduce((sum, p) => sum + p.amount, 0) || 0;
  const totalOverdue = payments?.filter(p => p.status === 'overdue').reduce((sum, p) => sum + p.amount, 0) || 0;

  const getStatusTag = (status: string) => {
    const statusMap: Record<string, { color: string; icon: React.ReactNode; text: string }> = {
      succeeded: {
        color: 'success',
        icon: <CheckCircleOutlined />,
        text: 'Pago',
      },
      pending: {
        color: 'warning',
        icon: <ClockCircleOutlined />,
        text: 'Pendente',
      },
      overdue: {
        color: 'error',
        icon: <CloseCircleOutlined />,
        text: 'Vencido',
      },
      refunded: {
        color: 'default',
        icon: <CloseCircleOutlined />,
        text: 'Reembolsado',
      },
    };

    const statusInfo = statusMap[status] || { color: 'default', icon: null, text: status };

    return (
      <Tag color={statusInfo.color} icon={statusInfo.icon}>
        {statusInfo.text}
      </Tag>
    );
  };

  const getBillingTypeIcon = (billingType: string) => {
    const iconMap: Record<string, React.ReactNode> = {
      CREDIT_CARD: <CreditCardOutlined />,
      BOLETO: <BarcodeOutlined />,
      PIX: <QrcodeOutlined />,
    };

    return iconMap[billingType] || <DollarOutlined />;
  };

  const getBillingTypeText = (billingType: string) => {
    const textMap: Record<string, string> = {
      CREDIT_CARD: 'Cartão de Crédito',
      BOLETO: 'Boleto Bancário',
      PIX: 'PIX',
      UNDEFINED: 'Não definido',
    };

    return textMap[billingType] || billingType;
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value);
  };

  const handleDownloadInvoice = (payment: Payment) => {
    if (payment.invoice_url) {
      window.open(payment.invoice_url, '_blank');
    }
  };

  const handleDownloadBoleto = (payment: Payment) => {
    if (payment.bank_slip_url) {
      window.open(payment.bank_slip_url, '_blank');
    }
  };

  const handleCopyPix = (payment: Payment) => {
    if (payment.pix_qr_code_payload) {
      navigator.clipboard.writeText(payment.pix_qr_code_payload);
      // You can add a message.success() here
    }
  };

  const columns: ColumnsType<Payment> = [
    {
      title: 'Data',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date: string) => (
        <Tooltip title={dayjs(date).format('DD/MM/YYYY HH:mm')}>
          {dayjs(date).fromNow()}
        </Tooltip>
      ),
      width: 150,
    },
    {
      title: 'Produto',
      key: 'product',
      ellipsis: true,
      render: (_, payment) => payment.client_subscriptions?.products?.name || payment.description || 'Produto não identificado',
    },
    {
      title: 'Valor',
      dataIndex: 'amount',
      key: 'amount',
      render: (amount: number) => (
        <Text strong style={{ fontSize: 16 }}>
          {formatCurrency(amount)}
        </Text>
      ),
      width: 120,
    },
    {
      title: 'Forma de Pagamento',
      key: 'billing_type',
      render: (_, payment) => {
        const billingType = payment.billing_type || payment.payment_method || 'UNDEFINED';
        return (
          <Space>
            {getBillingTypeIcon(billingType)}
            {getBillingTypeText(billingType)}
          </Space>
        );
      },
      width: 180,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => getStatusTag(status),
      width: 120,
    },
    {
      title: 'Vencimento',
      dataIndex: 'due_date',
      key: 'due_date',
      render: (date: string) => date ? dayjs(date).format('DD/MM/YYYY') : '-',
      width: 120,
    },
    {
      title: 'Ações',
      key: 'actions',
      render: (_, payment) => (
        <Space>
          {payment.invoice_url && (
            <Tooltip title="Baixar Fatura">
              <Button
                icon={<DownloadOutlined />}
                size="small"
                onClick={() => handleDownloadInvoice(payment)}
              >
                Fatura
              </Button>
            </Tooltip>
          )}

          {payment.bank_slip_url && payment.status !== 'succeeded' && (
            <Tooltip title="Baixar Boleto">
              <Button
                icon={<BarcodeOutlined />}
                size="small"
                type="primary"
                onClick={() => handleDownloadBoleto(payment)}
              >
                Boleto
              </Button>
            </Tooltip>
          )}

          {payment.pix_qr_code_payload && payment.status !== 'succeeded' && (
            <Tooltip title="Copiar Código PIX">
              <Button
                icon={<QrcodeOutlined />}
                size="small"
                type="primary"
                onClick={() => handleCopyPix(payment)}
              >
                Copiar PIX
              </Button>
            </Tooltip>
          )}
        </Space>
      ),
      width: 200,
    },
  ];

  return (
    <div style={{ padding: '24px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <Title level={2} style={{ margin: 0, background: 'linear-gradient(135deg, #7c3aed, #ec4899)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
          Histórico de Pagamentos
        </Title>
        <Button
          type="primary"
          icon={<SyncOutlined spin={isSyncing} />}
          onClick={syncWithAsaas}
          loading={isSyncing}
        >
          Sincronizar com Asaas
        </Button>
      </div>

      {/* Statistics */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={8}>
          <Card>
            <Statistic
              title="Total Pago"
              value={totalPaid}
              precision={2}
              prefix="R$"
              valueStyle={{ color: '#3f8600' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card>
            <Statistic
              title="Pendente"
              value={totalPending}
              precision={2}
              prefix="R$"
              valueStyle={{ color: '#faad14' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card>
            <Statistic
              title="Vencido"
              value={totalOverdue}
              precision={2}
              prefix="R$"
              valueStyle={{ color: '#cf1322' }}
            />
          </Card>
        </Col>
      </Row>

      {/* Payments Table */}
      <Card>
        <Table<Payment>
          columns={columns}
          dataSource={payments}
          loading={isLoading && !!userId}
          rowKey="id"
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showTotal: (total) => `Total: ${total} pagamentos`,
          }}
          locale={{
            emptyText: (
              <Empty
                description="Nenhum pagamento encontrado"
                image={Empty.PRESENTED_IMAGE_SIMPLE}
              />
            ),
          }}
        />
      </Card>
    </div>
  );
};

export default PaymentHistoryPage;
